import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, useContext, useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { MessageResource } from '@medable/i18n-core';
import { Locale } from 'libs.i18n-core';
import { AssetTypes } from 'libs.localization.common';
import { useLocalization } from 'libs.localization.react';
import { I18nContextProvider, Spinner } from 'libs.nucleus';
import { NotFoundPage } from 'libs.react';
import { AuthContext } from 'libs.react_internal';
import { SitesWrapper } from './components/sites_wrapper';
import { AxonAccountProvider } from './contexts/axon_account/axon_account.context';
import { CortexSessionHandlerProvider } from './contexts/cortex_session_handler/cortex_session_handler.context';
import { EnvironmentsContext, EnvironmentsProvider } from './contexts/environments/environments.context';
import { OrgProvider } from './contexts/org/org.context';
import { SiteProvider } from './contexts/site/site.context';
import { StudyTrainingProvider } from './contexts/study_training/study_training.context';
import { UserLocaleProvider } from './contexts/user_locale/user_locale.context';
import { getApiInstance } from './helpers/api.helpers';
import { useLumosTrainingNavigation } from './hooks/use_lumos_training';
import { HelpSupportPage } from './pages/help_support/help_support.page';
import { LumosTraining } from './pages/lumos_training/lumos_training.page';
import { ParticipantDetailsPage } from './pages/participant_details/participant_details.page';
import { ParticipantResponseDetailPage } from './pages/participant_response_detail/participant_response_detail.page';
import { ParticipantsList } from './pages/participants/participants_list.page';
import { NonVisitDetailsPage } from './pages/visit_details/non_visit_details.page';
import { VisitDetailsPage } from './pages/visit_details/visit_details.page';
const StudiesDashboardPage = lazy(() => import('./pages/studies_dashboard/studies_dashboard.page'));
const UserTrainingReportsPage = lazy(() => import('./pages/user_training_reports/user_training_reports.page'));
window.getApiInstance = getApiInstance;
const ProvidersWrapper = ({ children }) => {
    const { user } = useContext(AuthContext);
    const { environments, isLoading: isLoadingEnvironments } = useContext(EnvironmentsContext);
    // Determine the locale to use
    const userLocale = Locale[user?.locale || 'en-US'];
    // if we find a china env, we'll use it to fetch backup translations
    const foundChinaEnv = environments?.find((env) => env.deployment?.region === 'cn');
    const [cortexTranslations, setCortexTranslations] = useState(null);
    const [isLoadingTranslationsFromOrg, setIsLoadingTranslationsFromOrg] = useState(false);
    const [cortexError, setCortexError] = useState(null);
    const localeAsset = {
        type: AssetTypes.PRODUCT_STRINGS,
        id: 'lumos',
        version: '1',
        human_id: 'product_strings.lumos~1',
    };
    const { isLoading: isLoadingLocalization, localizationEntry: localeResources } = useLocalization(localeAsset);
    // Firebase isn't available for China so we fetch from Cortex org
    // Assumptions for this logic, but we really need a better solution like a CDN available from China
    // 1. We're using a .cn TLD in mainland China
    // 2. Site users with a .cn environment only have studies they're conducting in zh-Hans-CN
    useEffect(() => {
        if (foundChinaEnv && userLocale && !cortexTranslations && !isLoadingTranslationsFromOrg && !cortexError) {
            setIsLoadingTranslationsFromOrg(true);
            setCortexError(null);
            getApiInstance(foundChinaEnv)
                .get(`/v2/translations/${userLocale}?namespaces[]=web-medable-for-sites`)
                .then((res) => {
                const translations = res?.data?.['web-medable-for-sites'] || {};
                // prostprocess all keys to replace - with .
                const processedTranslations = Object.fromEntries(Object.entries(translations).map(([key, value]) => [key.replace(/-/g, '.'), value]));
                setCortexTranslations(processedTranslations);
            })
                .catch((err) => {
                setCortexError(err);
                setCortexTranslations({});
            })
                .finally(() => setIsLoadingTranslationsFromOrg(false));
        }
    }, [foundChinaEnv, userLocale, cortexTranslations, isLoadingTranslationsFromOrg, cortexError]);
    // Show spinner while loading environments or translations
    if (isLoadingEnvironments || (foundChinaEnv ? isLoadingTranslationsFromOrg : isLoadingLocalization)) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    const messageProvider = (locale) => {
        let localeToUse = {};
        if (foundChinaEnv) {
            localeToUse = cortexTranslations || {};
        }
        else {
            localeToUse = localeResources?.localization_resources?.[locale]?.main || {};
        }
        return new MessageResource(locale, localeToUse);
    };
    return (_jsx(I18nContextProvider, { locale: userLocale, messageProvider: messageProvider, children: _jsx(CortexSessionHandlerProvider, { children: _jsx(AxonAccountProvider, { children: _jsx(SiteProvider, { children: _jsx(OrgProvider, { children: children }) }) }) }) }));
};
export const SiteApp = () => {
    const navigate = useNavigate();
    useLumosTrainingNavigation();
    return (_jsx(EnvironmentsProvider, { children: _jsx(ProvidersWrapper, { children: _jsxs(Routes, { children: [_jsx(Route, { path: 'training', element: _jsx(LumosTraining, {}) }), _jsx(Route, { index: true, path: 'dashboard', element: _jsx(StudiesDashboardPage, {}) }), _jsxs(Route, { path: '/study/:studyId', element: _jsx(UserLocaleProvider, { children: _jsx(StudyTrainingProvider, { children: _jsx(SitesWrapper, {}) }) }), children: [_jsx(Route, { index: true, path: 'participants', element: _jsx(ParticipantsList, {}) }), _jsx(Route, { path: 'participants/:participantId', element: _jsx(ParticipantDetailsPage, {}) }), _jsx(Route, { path: 'participants/:participantId/response/:responseId', element: _jsx(ParticipantResponseDetailPage, {}) }), _jsx(Route, { path: 'participants/:participantId/visit/:visitId', element: _jsx(VisitDetailsPage, {}) }), _jsx(Route, { path: 'participants/:participantId/non-visit/:groupId', element: _jsx(NonVisitDetailsPage, {}) }), _jsx(Route, { path: 'help-support', element: _jsx(HelpSupportPage, {}) }), _jsx(Route, { path: 'user-training-reports', element: _jsx(UserTrainingReportsPage, {}) })] }), _jsx(Route, { path: '*', element: _jsx(NotFoundPage, { goBack: () => navigate(-1) }) })] }) }) }));
};
export { SITE_FLAGS } from './constants/developer_flags.constants';
